import { FilledButton, HouseHeader, CustomPdfViewer, LabelText, Container } from 'components/Custom'
import { Box, Checkbox } from 'components/Material'
import { useEffect, useState } from 'react'
import {
  postAssignContractConfirmation,
  postContractToken
} from 'services/simulacao/assinaturaContrato'
import { useMutation } from '@tanstack/react-query'
import { useSnackbarAlertContext } from 'contexts/SnackbarContext'
import { useLocation, useNavigate } from 'react-router-dom'

const ContractView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { code } = location.state || {}

  const [checkboxValue, setCheckboxValue] = useState(false)
  const { showAlert } = useSnackbarAlertContext()

  useEffect(() => {
    const sn_assinado = localStorage.getItem('sn_assinado')
    const dh_assinatura = localStorage.getItem('dh_assinatura')
    const pdf = localStorage.getItem('pdf')
    const encrypted = localStorage.getItem('encrypted')

    if (!sn_assinado || !dh_assinatura || !pdf || !encrypted) {
      localStorage.clear()
      navigate('/permissao-token-contrato')
      return
    }
  }, [navigate])

  const pdf = localStorage.getItem('pdf')
  const encrypted = localStorage.getItem('encrypted')
  const storedSignatureStatus = localStorage.getItem('sn_assinado')
  const storedSignatureDate = localStorage.getItem('dh_assinatura')

  // Formatando a data armazenada para o formato brasileiro
  const formatStoredDate = (dateString: string) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      console.error('Error formatting date:', error)
      return ''
    }
  }

  const [contractSigned, setContractSigned] = useState<boolean>(storedSignatureStatus === 'S')
  const [acceptedDate, setAcceptedDate] = useState<string>(
    formatStoredDate(storedSignatureDate || '')
  )

  useEffect(() => {
    if (!pdf) {
      showAlert({
        message: 'Nenhum contrato foi encontrado',
        statusType: 'error',
        hasCloseButton: false
      })
    }
  }, [pdf])

  const postAssignContractConfirmationMutation = useMutation({
    mutationFn: async () => {
      const response = await postAssignContractConfirmation({ id_concc030_cript: encrypted || '' })
      return response
    }
  })

  const postContratoMutation = useMutation({ mutationFn: postContractToken })

  const handleAcceptTerms = async () => {
    if (checkboxValue && encrypted && pdf) {
      try {
        await postAssignContractConfirmationMutation.mutateAsync()
        const assignedContrat = await postContratoMutation.mutateAsync({
          codigo: code,
          id_concc030_cript: encrypted
        })
        console.log('assignedContrat', assignedContrat)
        // Atualizando localStorage
        localStorage.setItem('sn_assinado', assignedContrat.sn_assinado || 'S')
        localStorage.setItem(
          'dh_assinatura',
          assignedContrat.dh_assinatura || new Date().toISOString()
        )
        localStorage.setItem('pdf', assignedContrat.report)
        setContractSigned(true)
        setAcceptedDate(formatStoredDate(assignedContrat.dh_assinatura || new Date().toISOString()))

        showAlert({
          message: 'Contrato aceito com sucesso',
          statusType: 'success',
          hasCloseButton: false
        })
      } catch (error: any) {
        console.error('Erro detalhado:', error)
        // Se o erro for 404 ou outro erro esperado que indica que deu certo no backend
        if (error?.response?.status === 404 || error?.response?.status === 204) {
          // Consideramos como sucesso mesmo com erro 404/204
          setContractSigned(true)
          const now = new Date().toISOString()
          localStorage.setItem('sn_assinado', 'S')
          localStorage.setItem('dh_assinatura', now)
          setAcceptedDate(formatStoredDate(now))
          showAlert({
            message: 'Contrato aceito com sucesso',
            statusType: 'success',
            hasCloseButton: false
          })
        } else {
          setCheckboxValue(false)
          setContractSigned(false)
          showAlert({
            message: 'Erro ao aceitar o contrato',
            statusType: 'error',
            hasCloseButton: false
          })
        }
      }
    }
  }

  const handleDownload = () => {
    const binaryString = window.atob(pdf || '')
    const binaryLen = binaryString.length
    const bytes = new Uint8Array(binaryLen)
    for (let i = 0; i < binaryLen; i++) {
      const ascii = binaryString.charCodeAt(i)
      bytes[i] = ascii
    }
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'file.pdf'
    link.click()
  }

  const isLoading =
    postAssignContractConfirmationMutation.isPending || postContratoMutation.isPending

  return (
    <Container leftLogo={true} route="/menu" leftText="Consórcio BB">
      <HouseHeader defaultPadding title="Contrato" />

      <Box className="w-full flex flex-col items-center p-5 ">
        <Box className="p-2 w-full max-w-[800px] rounded-xl border-solid border border-gray-cac flex flex-col justify-center items-center bg-[white] md:h-[calc(100vh-379px)] h-[calc(100vh-353px)]">
          <CustomPdfViewer file={pdf || ''} />
        </Box>
      </Box>
      {/* Footer */}
      <Box className="sticky bottom-0 p-5 pb-8 flex flex-col w-full items-center bg-[white] border-solid border-t border-gray-cac">
        <Box className="w-full max-w-[800px] justify-center items-center">
          <LabelText text="Termo de aceite" typeStyle="large" />
          {!contractSigned ? (
            <Box className="w-full flex justify-start items-center mb-4">
              <Checkbox
                disabled={!pdf || contractSigned || isLoading}
                checked={checkboxValue}
                onChange={() => setCheckboxValue(!checkboxValue)}
              />
              <LabelText
                text="Li e concordo com os termos da Proposta de Adesão"
                typeStyle="large"
                disabled={!pdf || contractSigned || isLoading}
              />
            </Box>
          ) : (
            <Box className="w-full mt-2 mb-4">
              <LabelText
                text={`Contrato aceito em ${acceptedDate}`}
                typeStyle="medium"
                className="text-green-500"
              />
            </Box>
          )}
          <Box className="w-full flex flex-row flex-wrap justify-center md:justify-end items-center gap-3 mb-2">
            {!contractSigned && (
              <Box className="flex justify-center items-center w-full max-w-[350px]">
                <FilledButton
                  disabled={!checkboxValue || !pdf || isLoading || contractSigned}
                  onClick={handleAcceptTerms}
                  loading={isLoading}
                >
                  Aceitar termos
                </FilledButton>
              </Box>
            )}
            <Box className="flex justify-center items-center w-full max-w-[350px]">
              <FilledButton
                disabled={!pdf || isLoading}
                onClick={handleDownload}
                loading={false}
              >
                Download
              </FilledButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  )
}

export default ContractView
